import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Monitor, Server, Shield, Zap, Database, Cloud, Network, Lock, Settings, Users, BarChart, ArrowRight, Layers, Cpu, Wifi, Globe, Battery, Clock, FileCheck, ChevronRight, ChevronLeft, CheckCircle, Lightbulb, Workflow, Repeat, Gauge, Play, Pause } from 'lucide-react';
import { Link } from 'react-router-dom';

const ProxyPower: React.FC = () => {
  useEffect(() => {
    document.title = 'Proxy Power - BaaS System Architecture';
  }, []);

  const [activeComponent, setActiveComponent] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const components = [
    {
      icon: <Smartphone className="w-12 h-12 text-white" />,
      title: "User Mobile App",
      description: "Application mobile intuitive pour les utilisateurs finaux",
      image: "https://images.pexels.com/photos/6633920/pexels-photo-6633920.jpeg",
      features: [
        "Localisation des stations en temps réel",
        "Réservation et paiement de recharge",
        "Historique des transactions",
        "Suivi de la consommation"
      ],
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Monitor className="w-12 h-12 text-white" />,
      title: "Management Platform",
      description: "Plateforme complète de gestion et supervision",
      image: "https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg",
      features: [
        "Tableau de bord en temps réel",
        "Gestion de la flotte de batteries",
        "Suivi des performances",
        "Rapports analytiques"
      ],
      color: "from-primary-600 to-primary-700"
    },
    {
      icon: <Smartphone className="w-12 h-12 text-white" />,
      title: "Merchant App",
      description: "Application dédiée aux partenaires et opérateurs",
      image: "https://images.pexels.com/photos/6633926/pexels-photo-6633926.jpeg",
      features: [
        "Gestion des stations",
        "Suivi des transactions",
        "Gestion des stocks",
        "Rapports financiers"
      ],
      color: "from-green-500 to-green-600"
    }
  ];

  const systemBenefits = [
    {
      icon: <Battery className="w-10 h-10 text-primary-600" />,
      title: "Gestion Intelligente",
      description: "Suivi en temps réel de l'état de charge et de la santé des batteries",
      stats: "99.9% disponibilité"
    },
    {
      icon: <Clock className="w-10 h-10 text-primary-600" />,
      title: "Optimisation du Temps",
      description: "Réduction du temps d'attente grâce à la réservation en ligne",
      stats: "Gain de 15 min"
    },
    {
      icon: <FileCheck className="w-10 h-10 text-primary-600" />,
      title: "Traçabilité Complète",
      description: "Historique des transactions et des échanges de batteries",
      stats: "100% traçabilité"
    },
    {
      icon: <Gauge className="w-10 h-10 text-primary-600" />,
      title: "Performance Optimisée",
      description: "Algorithmes d'optimisation pour maximiser l'autonomie",
      stats: "+30% d'autonomie"
    }
  ];

  const nextComponent = () => {
    setActiveComponent((prev) => (prev + 1) % components.length);
  };

  const prevComponent = () => {
    setActiveComponent((prev) => (prev - 1 + components.length) % components.length);
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="absolute inset-0 overflow-hidden opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
                BaaS System Architecture
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                Notre architecture Battery-as-a-Service (BaaS) intègre trois applications interconnectées pour une gestion optimale de la mobilité électrique.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-800 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Demander une Démo
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <Link 
                  to="/stations"
                  className="inline-flex items-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30"
                >
                  Voir les Stations
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-2xl transform transition-all duration-500 hover:shadow-2xl hover:border-white/30 overflow-hidden">
                <div className="relative aspect-w-16 aspect-h-9 rounded-xl overflow-hidden mb-4">
                  <img 
                    src="https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg" 
                    alt="BaaS Architecture"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl font-bold text-white mb-1">Architecture Innovante</h3>
                    <p className="text-sm text-white/80">Conçue pour l'Afrique, déployée globalement</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {components.map((component, index) => (
                    <div key={index} className={`p-3 rounded-lg bg-gradient-to-br ${component.color} text-white text-center`}>
                      {component.icon}
                      <div className="text-xs mt-1 font-medium">{component.title.split(' ')[0]}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-4 max-w-xs transform hover:translate-x-2 hover:translate-y-2 transition-all duration-300">
                <div className="flex items-center mb-2">
                  <Zap size={18} className="text-primary-500 mr-2" />
                  <span className="font-medium text-gray-900">Technologie Avancée</span>
                </div>
                <p className="text-gray-600 text-sm">
                  Architecture cloud native avec microservices et API sécurisées
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Component Showcase */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Composants Principaux
            </h2>
            <p className="text-lg text-neutral-600">
              Une architecture modulaire et évolutive pour tous les acteurs de l'écosystème
            </p>
          </div>

          <div className="relative">
            {/* Navigation Buttons */}
            <button 
              onClick={prevComponent}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors group"
              aria-label="Composant précédent"
            >
              <ChevronLeft size={24} className="text-primary-600 group-hover:-translate-x-1 transition-transform" />
            </button>
            
            <button 
              onClick={nextComponent}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors group"
              aria-label="Composant suivant"
            >
              <ChevronRight size={24} className="text-primary-600 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Component Display */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Image Side */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-xl border border-gray-100 overflow-hidden">
                <div className="relative aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                  <img 
                    src={components[activeComponent].image} 
                    alt={components[activeComponent].title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${components[activeComponent].color} opacity-60`}></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/20 backdrop-blur-sm p-6 rounded-xl border border-white/30 transform transition-all duration-500 hover:scale-105">
                      <div className="p-4 bg-white rounded-full inline-block mb-4">
                        {components[activeComponent].icon}
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">{components[activeComponent].title}</h3>
                      <p className="text-white/90">{components[activeComponent].description}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Side */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <div className={`p-3 rounded-lg bg-gradient-to-br ${components[activeComponent].color} mr-3`}>
                    {components[activeComponent].icon}
                  </div>
                  {components[activeComponent].title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {components[activeComponent].features.map((feature, idx) => (
                    <div key={idx} className="flex items-start p-4 bg-gray-50 rounded-lg hover:bg-primary-50 transition-colors transform hover:translate-y-[-2px]">
                      <CheckCircle size={20} className="text-primary-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center mt-8 space-x-2">
              {components.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveComponent(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === activeComponent ? 'bg-primary-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Voir composant ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Voir le Système en Action
            </h2>
            <p className="text-lg text-neutral-600">
              Découvrez comment notre architecture BaaS fonctionne en temps réel
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-xl border border-primary-100 overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 bg-gray-900 rounded-lg overflow-hidden relative">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-900 to-primary-700">
                <div className="text-center">
                  <button 
                    onClick={toggleVideo}
                    className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-primary-600 hover:bg-gray-100 transition-colors mb-4 mx-auto"
                  >
                    {isVideoPlaying ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
                  </button>
                  <h3 className="text-2xl font-bold text-white mb-2">Démonstration BaaS</h3>
                  <p className="text-white/80 max-w-md mx-auto">
                    Voyez comment notre système connecte les utilisateurs, les opérateurs et les gestionnaires en temps réel
                  </p>
                </div>
                {/* Placeholder for actual video */}
                <video 
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover opacity-50"
                  poster="https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg"
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                >
                  <source src="#" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Architecture Diagram */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Architecture Système
            </h2>
            <p className="text-lg text-neutral-600">
              Une architecture moderne et scalable pour supporter votre croissance
            </p>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-xl border border-primary-100">
            <div className="relative">
              {/* Architecture Diagram */}
              <div className="relative p-8 overflow-hidden">
                {/* Central Hub */}
                <div className="flex justify-center mb-12">
                  <div className="relative">
                    <div className="w-40 h-40 bg-gradient-to-br from-primary-600 to-primary-800 rounded-full flex items-center justify-center shadow-xl transform transition-transform hover:scale-105">
                      <div className="text-white text-center">
                        <Layers size={40} className="mx-auto mb-2" />
                        <div className="font-bold">BaaS Core</div>
                        <div className="text-xs opacity-75">Central System</div>
                      </div>
                    </div>
                    {/* Connection lines */}
                    <div className="absolute top-1/2 -left-[200px] w-[200px] border-t-2 border-dashed border-primary-300"></div>
                    <div className="absolute top-1/2 -right-[200px] w-[200px] border-t-2 border-dashed border-primary-300"></div>
                    <div className="absolute -bottom-[100px] left-1/2 h-[100px] border-l-2 border-dashed border-primary-300"></div>
                  </div>
                </div>
                
                {/* Connected Systems */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {components.map((component, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className={`w-28 h-28 bg-gradient-to-br ${component.color} rounded-2xl flex items-center justify-center shadow-lg mb-4 transform transition-transform hover:scale-110`}>
                        <div className="text-white">
                          {component.icon}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{component.title}</h3>
                      <p className="text-sm text-gray-600 text-center">{component.description}</p>
                    </div>
                  ))}
                </div>
                
                {/* Data Flow Indicators */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-[140px] left-1/4 transform -translate-x-1/2">
                    <div className="flex items-center">
                      <Lock size={16} className="text-primary-500" />
                      <span className="ml-1 text-xs text-primary-600">Secure Data</span>
                    </div>
                  </div>
                  <div className="absolute top-[140px] right-1/4 transform translate-x-1/2">
                    <div className="flex items-center">
                      <Settings size={16} className="text-primary-500" />
                      <span className="ml-1 text-xs text-primary-600">API Gateway</span>
                    </div>
                  </div>
                  <div className="absolute bottom-[140px] left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center">
                      <Users size={16} className="text-primary-500" />
                      <span className="ml-1 text-xs text-primary-600">User Management</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Benefits */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Bénéfices du Système
            </h2>
            <p className="text-lg text-neutral-600">
              Des avantages concrets pour tous les acteurs de l'écosystème
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group">
                <div className="mb-4 transform group-hover:scale-110 transition-transform">
                  <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center">
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{benefit.description}</p>
                <div className="text-primary-600 font-semibold">{benefit.stats}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Architecture Visualization */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Visualisation Interactive
            </h2>
            <p className="text-lg text-neutral-600">
              Explorez notre architecture BaaS et ses interactions
            </p>
          </div>

          <div className="bg-gradient-to-br from-primary-900 to-primary-800 rounded-xl p-8 shadow-xl text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
            <div className="absolute inset-0 overflow-hidden opacity-10">
              <div className="absolute top-0 left-0 w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: '60px 60px'
              }}></div>
            </div>
            
            <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="aspect-w-16 aspect-h-9 bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20">
                  <img 
                    src="https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg" 
                    alt="Architecture BaaS"
                    className="w-full h-full object-cover opacity-75"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Layers size={64} className="mx-auto mb-4 text-white" />
                      <h3 className="text-2xl font-bold text-white">Architecture BaaS</h3>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                  <h3 className="text-lg font-bold mb-2 flex items-center">
                    <Repeat size={20} className="mr-2 text-accent-300" />
                    Cycle de Vie des Données
                  </h3>
                  <div className="flex items-center justify-between text-white/60 text-sm">
                    <span>Collecte</span>
                    <ChevronRight size={16} />
                    <span>Traitement</span>
                    <ChevronRight size={16} />
                    <span>Analyse</span>
                  </div>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                  <h3 className="text-lg font-bold mb-2 flex items-center">
                    <Wifi size={20} className="mr-2 text-accent-300" />
                    Connectivité IoT
                  </h3>
                  <p className="text-white/80 text-sm">
                    Communication en temps réel avec le système central
                  </p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                  <h3 className="text-lg font-bold mb-2 flex items-center">
                    <Globe size={20} className="mr-2 text-accent-300" />
                    Déploiement Global
                  </h3>
                  <p className="text-white/80 text-sm">
                    Architecture multi-langues et conforme aux réglementations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Stack Technologique
            </h2>
            <p className="text-lg text-neutral-600">
              Des technologies modernes pour une solution robuste et évolutive
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-xl border border-primary-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl border border-primary-100 transform transition-all hover:translate-y-[-4px] hover:shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <Smartphone size={20} className="text-primary-600 mr-2" />
                  Frontend
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">React Native</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">React.js</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">TypeScript</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">TailwindCSS</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl border border-primary-100 transform transition-all hover:translate-y-[-4px] hover:shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <Server size={20} className="text-primary-600 mr-2" />
                  Backend
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Node.js</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Express.js</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">GraphQL</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Microservices</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl border border-primary-100 transform transition-all hover:translate-y-[-4px] hover:shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <Database size={20} className="text-primary-600 mr-2" />
                  Infrastructure
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">PostgreSQL</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Redis</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Docker</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg shadow-sm text-center">
                    <span className="text-sm font-medium">Kubernetes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Prêt à Démarrer ?
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                Découvrez comment notre solution BaaS peut transformer votre activité et propulser votre croissance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact"
                  className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-800 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Demander une Démo
                  <ArrowRight size={20} className="ml-2 inline-block" />
                </Link>
                <Link 
                  to="/contact"
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30"
                >
                  Nous Contacter
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                <div className="aspect-w-1 aspect-h-1 mb-3">
                  <img 
                    src="https://images.pexels.com/photos/6633920/pexels-photo-6633920.jpeg" 
                    alt="User App"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-bold">User App</h3>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                <div className="aspect-w-1 aspect-h-1 mb-3">
                  <img 
                    src="https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg" 
                    alt="Management Platform"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-bold">Management</h3>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                <div className="aspect-w-1 aspect-h-1 mb-3">
                  <img 
                    src="https://images.pexels.com/photos/6633926/pexels-photo-6633926.jpeg" 
                    alt="Merchant App"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-bold">Merchant App</h3>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 transform transition-all hover:translate-y-[-4px]">
                <div className="aspect-w-1 aspect-h-1 mb-3">
                  <img 
                    src="https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg" 
                    alt="BaaS Core"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-lg font-bold">BaaS Core</h3>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProxyPower;